import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import { Helmet } from "react-helmet";
import { getAllClientsStats, getAllOwnerStats, getAllServiceAgentStats } from "redux/features/auth/authSlice";
import CommonLayout from "./DashboardMenu";

import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js";
import { useParams } from "react-router-dom";

export interface AdminProfileProps {
  className?: string;
}

const AdminProfile: FC<AdminProfileProps> = ({ className = "" }) => {
  ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);
  const dispatch = useAppDispatch();

  const stats = useAppSelector((state) => state?.auth?.userStats);
  const { user } = useParams();

  useEffect(() => {
    if (user === "clients") {
      dispatch(getAllClientsStats());
    } else if (user === "agents") {
      dispatch(getAllServiceAgentStats());
    } else if (user === "owner") {
      dispatch(getAllOwnerStats());
    }
  }, [dispatch, user]);

  const total = Object.values(stats || {}).reduce((acc: number, value: number) => acc + value, 0);
  const months = Object.keys(stats || {});
  const userData = {
    labels: months.map((month) => `${month}`),
    datasets: [
      {
        label: "Statistiques",
        data: Object.values(stats || {}),
        backgroundColor: "#36A2EB",
      },
    ],
  };

  const options = {
    scales: {
      x: {
        stacked: true,
        precision: 0,
        ticks: {
          font: {
            weight: "bold" as "bold",
          },
        },
      },
      y: {
        stacked: true,
        precision: 0,
        ticks: {
          stepSize: 1,
          font: {
            weight: "bold" as "bold",
          },
          callback: function (value: any) {
            return value.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
          },
        },
      },
    },
  };

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>

      <CommonLayout>
        <div className="space-y-8">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Statistiques du {user}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Analyse des données pour la période sélectionnée
              </p>
            </div>
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-2xl shadow-lg">
              <p className="text-sm font-medium">Total</p>
              <p className="text-2xl font-bold">{total.toLocaleString()}</p>
            </div>
          </div>

          {/* Chart Section */}
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-3xl p-8 shadow-xl border border-gray-200/50 dark:border-gray-700/50">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Évolution mensuelle</h3>
              <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
            </div>
            <div className="h-96">
              <Bar data={userData} options={{ ...options, maintainAspectRatio: false }}></Bar>
            </div>
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AdminProfile;
